import { z } from "zod";
import { isValidIBAN } from "ibantools";
import { fileSchema } from "@/src/lib/schemas";
import { locationSchema } from "../../Bookings/EditBookingCharterInfo/bookingCharterInfo.schema";

export const OperatorForm = z.object({
  operator: z.object({
    name: z.string().min(1),
    contactPerson: z.string(),
    email: z.string().email(),
    email2: z.string().email().or(z.literal("")),
    phone: z.string(),
    address: z.string(),
    website: z
      .string()
      .regex(/((https?:\/\/)?\w*\.\w+)?/)
      .optional(),
    rating: z.number().int().min(1).max(10),
    ratingAnchor: z.number().int().min(1).max(10),
    ratingHelm: z.number().int().min(1).max(10),
  }),
  labels: z
    .array(z.object({ id: z.string().uuid(), value: z.string() }))
    .max(2)
    .optional(),
  visibility: z.object({
    helm_app_hide_skippered: z.boolean(),
    helm_app_bm_over_ns: z.boolean(),
    helm_app_yf_over_cya: z.boolean(),
    helm_app_cya: z.enum(["hidden", "visible"]),
    helm_app_ankor: z.enum(["hidden", "visible"]),
    helm_app_ns: z.enum(["hidden", "visible"]),
    helm_app_bm: z.enum(["hidden", "visible"]),
    helm_app_yf: z.enum(["hidden", "visible"]),
    helm_app_helm: z.enum(["hidden", "visible"]),
    helm_cya: z.enum(["hidden", "visible"]),
    helm_ankor: z.enum(["hidden", "visible"]),
    helm_ns: z.enum(["hidden", "visible"]),
    helm_bm: z.enum(["hidden", "visible"]),
    helm_yf: z.enum(["hidden", "visible"]),
    helm_helm: z.enum(["hidden", "visible"]),
    helm_hide_skippered: z.boolean(),
    helm_bm_over_ns: z.boolean(),
    helm_yf_over_cya: z.boolean(),
    anchor_cya: z.enum(["hidden", "visible"]),
    anchor_ankor: z.enum(["hidden", "visible"]),
    anchor_ns: z.enum(["hidden", "visible"]),
    anchor_bm: z.enum(["hidden", "visible"]),
    anchor_yf: z.enum(["hidden", "visible"]),
    anchor_helm: z.enum(["hidden", "visible"]),
    anchor_hide_skippered: z.boolean(),
    anchor_bm_over_ns: z.boolean(),
    anchor_yf_over_cya: z.boolean(),
  }),
  transferDetails: z.object({
    recipientName: z.string().min(1).or(z.literal("")),
    accountNumber: z
      .string()
      .refine((s) => isValidIBAN(s), { message: "Invalid IBAN" })
      .or(z.literal("")),
    currencyCode: z.string().or(z.literal("")),
  }),
  operatorPayments: z.object({
    commission: z.object({
      value: z.number(),
      type: z.literal("percentage").or(z.literal("fixed")),
    }),
    customDiscount: z.number(),
    paymentSchedule: z
      .array(
        z.object({
          amount: z.number(),
          days: z.number().int().nonnegative(),
          type: z.enum(["on confirmation", "after confirmation", "before booking date"]),
        }),
      )
      .refine(
        (arr) => {
          const totalAmount = arr.reduce((acc, item) => acc + item.amount, 0);
          return totalAmount === 100;
        },
        { message: "Total amount should be 100" },
      ),
  }),
  termsAndConditions: fileSchema.nullable(),
  goodToKnow: z.object({
    type: z.literal("simple").or(z.literal("charter-dependent")),
    simple: z.string(),
    skippered: z.string(),
    crewed: z.string(),
    bareboat: z.string(),
  }),
  boardingInformations: z.array(
    z.object({
      description: z.string(),
      file: fileSchema.nullable(),
      locationId: z.string().uuid(),
    }),
  ),
  notes: z.string(),
});

export type OperatorForm = z.infer<typeof OperatorForm>;

export const emptyOperatorForm: (locations: string[]) => OperatorForm = (locations: string[]) => ({
  operator: {
    name: "",
    contactPerson: "",
    email: "",
    email2: "",
    phone: "",
    address: "",
    website: "",
    rating: 5,
    ratingAnchor: 5,
    ratingHelm: 5,
  },
  labels: [],
  visibility: {
    helm_app_cya: "hidden",
    helm_app_ankor: "hidden",
    helm_app_ns: "hidden",
    helm_app_bm: "hidden",
    helm_app_yf: "visible",
    helm_app_helm: "visible",
    helm_app_hide_skippered: true,
    helm_app_bm_over_ns: true,
    helm_app_yf_over_cya: true,
    helm_hide_skippered: true,
    helm_bm_over_ns: true,
    helm_yf_over_cya: true,
    helm_cya: "hidden",
    helm_ankor: "hidden",
    helm_ns: "hidden",
    helm_bm: "hidden",
    helm_yf: "visible",
    helm_helm: "visible",
    anchor_hide_skippered: true,
    anchor_bm_over_ns: true,
    anchor_yf_over_cya: false,
    anchor_cya: "hidden",
    anchor_ankor: "hidden",
    anchor_ns: "hidden",
    anchor_bm: "hidden",
    anchor_yf: "hidden",
    anchor_helm: "visible",
  },
  transferDetails: {
    recipientName: "",
    accountNumber: "",
    currencyCode: "",
  },
  operatorPayments: {
    commission: {
      type: "percentage",
      value: 0,
    },
    customDiscount: 0,
    paymentSchedule: [
      {
        paymentTime: "",
      } as any,
    ],
  },
  termsAndConditions: null,
  goodToKnow: {
    type: "simple",
    simple: "",
    skippered: "",
    crewed: "",
    bareboat: "",
  },
  boardingInformations: locations.map((locationId) => ({
    description: "",
    file: null,
    locationId,
  })),
  notes: "",
});

export const BoardingInfoForm = z.object({
  description: z.string().min(3),
  file: fileSchema.nullable(),
  location: z.object({
    id: z.string().min(1),
    names: z.string().min(1),
  }),
});

export const emptyBoardingInfoForm: BoardingInfoForm = {
  description: "",
  file: null,
  location: {
    id: "",
    names: "",
  },
};

export type BoardingInfoForm = z.infer<typeof BoardingInfoForm>;
